import {
  assertNotEmpty,
  dateUtils,
  listUtils,
  type Optional,
} from '@festina-life/core'
import dayjs, { type Dayjs } from 'dayjs'

import { NO_DATE_SYMBOL } from '../../util/constants'
import {
  INDICATOR_PADDING,
  MAX_TIME_FOR_INDICATORS_YEARS,
} from '../templateTimeline/constants'
import type { TimelineUnit } from '../templateTimeline/types'

/**
 * Given a start data and an end date, this function will calculate the most appropriate unit (year, month, day) for showing the
 * timeline, using the largest possible unit.
 */
export const getTimelineUnit = (
  fromStartDate: Date,
  toEndDate: Date,
): TimelineUnit => {
  const date1 = dayjs(fromStartDate)
  const date2 = dayjs(toEndDate)
  const isDiffInYears = date2.diff(date1, 'years') >= 1
  const isDiffInMonths = date2.diff(date1, 'months') >= 1

  if (isDiffInYears) {
    return 'years'
  } else if (isDiffInMonths) {
    return 'months'
  } else {
    return 'days'
  }
}

/**
 *  Given a start date and an end date with a timeline unit, this function will return start dates and end dates adjusted to
 *  the beginning and end of the timeline unit, eg. beginning of the year and end of the year such that the beginning and end date
 *  are included withing the returned time span.
 */
export const getBoundingTime = (dates: Date[], unit: TimelineUnit) => {
  const startDate = dayjs.min(...dates.map(dayjs))?.utc()
  const endDate = dayjs.max(...dates.map(dayjs))?.utc()

  if (!startDate || !endDate) {
    throw new Error('No dates provided')
  }

  switch (unit) {
    case 'days':
      return {
        from: startDate.startOf('week').toDate(),
        to: endDate.endOf('week').toDate(),
      }
    case 'months':
      return {
        from: startDate.startOf('month').toDate(),
        to: endDate.endOf('month').toDate(),
      }
    case 'years': // years
      return {
        from: startDate.startOf('year').toDate(),
        to: endDate.endOf('year').toDate(),
      }
    default:
      throw new Error('Unknown unit of time')
  }
}

/** Given a date, will return the correct indicator label based on the desired unit. */
export const getIndicatorLabel = (forDate: Date, unit: TimelineUnit) => {
  const day = dayjs(forDate)

  switch (unit) {
    case 'years':
      return day.format('YYYY')
    case 'months':
      return day.format('MMM')
    case 'days':
      return day.format('L')
  }
}

type TimeIndicator = {
  date: Date
  label: string
}

/**
 * Given a start time, an end time and a unit, this function will return all the time indicators that should be shown on
 * a timeline.
 */
export const getTimeIndicators = ({
  fromDate,
  toDate,
  unit,
}: {
  fromDate: Date
  toDate: Date
  unit: TimelineUnit
}): TimeIndicator[] => {
  const indicators: TimeIndicator[] = []

  const endDate = dayjs(toDate).utc()
  const addStepDeltaInSeconds = {
    days: (toDate: Dayjs) => toDate.add(dayjs.duration({ days: 1 })),
    months: (toDate: Dayjs) =>
      toDate.add(dayjs.duration({ days: 31 })).startOf('month'),
    years: (toDate: Dayjs) => toDate.add(dayjs.duration({ years: 1 })),
  }

  const startDate = dayjs(fromDate).utc()

  if (endDate.diff(startDate, 'years') < MAX_TIME_FOR_INDICATORS_YEARS) {
    for (
      let currentDate = startDate;
      currentDate.isBefore(endDate) || currentDate.isSame(endDate);
      currentDate = addStepDeltaInSeconds[unit](currentDate)
    ) {
      indicators.push({
        date: currentDate.toDate(),
        label: getIndicatorLabel(currentDate.toDate(), unit),
      })
    }
  }

  return indicators
}

/**
 * Given a list of time indicators, a length in pixels for the indicators timespan and a length for the labels, this function will filter out unnecessary
 * indicators so that their labels don't overlap in the timeline chart.
 */
export const filterOverlappingTimeIndicators = ({
  labelWidthInPixels,
  timeIndicators,
  timeToPxFn,
}: {
  timelineLengthInPx: number
  labelWidthInPixels: number
  timeIndicators: TimeIndicator[]
  timeToPxFn: (d: Date) => number
}) => {
  const filteredIndicators: TimeIndicator[] = []
  let lastIndicator: TimeIndicator | null = null

  if (timeIndicators.length) {
    for (const indicator of timeIndicators) {
      const indicatorPosition = timeToPxFn(
        dateUtils.dateUtcAsLocal(indicator.date).toDate(),
      )

      const lastIndicatorPosition = lastIndicator
        ? timeToPxFn(dateUtils.dateUtcAsLocal(lastIndicator.date).toDate())
        : 0

      if (
        indicatorPosition -
          lastIndicatorPosition -
          labelWidthInPixels -
          INDICATOR_PADDING >
        0
      ) {
        filteredIndicators.push(indicator)
        lastIndicator = indicator
      }
    }
  }

  return filteredIndicators
}

/**
 * Given a list of items, computes the earliest and latest version dates.
 * Items without a fromDate are excluded from the computation.
 * If no items have a fromDate, the defaultDate is used for both.
 */
export const getVersionDateBoundaries = <T>({
  items,
  getFromDate,
  defaultDate,
}: {
  items: T[]
  getFromDate: (item: T) => string | null | undefined
  defaultDate: string
}): { earliestDate: Date; latestDate: Date } => {
  const boundaries = items.reduce(
    (acc, item) => {
      const fromDate = getFromDate(item)
      if (!fromDate) {
        return acc
      }
      return {
        earliestDate: dayjs(fromDate).isBefore(acc.earliestDate)
          ? fromDate
          : acc.earliestDate,
        latestDate: dayjs(fromDate).isAfter(acc.latestDate)
          ? fromDate
          : acc.latestDate,
      }
    },
    { earliestDate: defaultDate, latestDate: defaultDate },
  )

  return {
    earliestDate: dateUtils.dateUtcAsLocal(boundaries.earliestDate).toDate(),
    latestDate: dateUtils.dateUtcAsLocal(boundaries.latestDate).toDate(),
  }
}

/**
 * Computes the intervals a timeline is drawn as hidden over. An item that hides the
 * timeline opens an interval on its from date, running until the next date brought into
 * effect by an item that does not hide it, or to the end of the timeline when nothing
 * closes it.
 */
export const getHiddenTimeIntervals = <T>({
  items,
  getFromDate,
  isHideItem,
  endDate,
}: {
  items: T[]
  getFromDate: (item: T) => Optional<string>
  isHideItem: (item: T) => boolean
  endDate: Date
}) => {
  const itemsByDate = listUtils.groupBy(
    items,
    (item) => getFromDate(item) ?? NO_DATE_SYMBOL,
  )

  // The last item on a date is the one in effect from it, so it decides whether the
  // timeline is hidden from that date onwards.
  const isDateHidden = (dateItems: T[]) => {
    const effectiveItem = dateItems.at(-1)
    return !!effectiveItem && isHideItem(effectiveItem)
  }

  const sortedDateEntries = [...itemsByDate]
    .map(
      ([date, dateItems]) =>
        [typeof date === 'symbol' ? null : date, dateItems] as const,
    )
    .sort((a, b) => dateUtils.compare(a[0], b[0]))

  return sortedDateEntries.reduce(
    // oxlint-disable-next-line max-params
    (intervals, [itemDate, dateItems], currentIndex) => {
      if (isDateHidden(dateItems)) {
        const intervalEndDate = sortedDateEntries
          .slice(currentIndex + 1)
          .find(([, laterItems]) => !isDateHidden(laterItems))?.[0]
        const endDateString =
          intervalEndDate ?? dateUtils.dateAsServerStr(endDate)
        assertNotEmpty(endDateString)

        intervals.push([itemDate, endDateString])
      }

      return intervals
    },
    [] as [string | null, string][],
  )
}
